/**
 * Agreement Detail Dialog - View and manage premium partner agreement details
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Stack,
  Alert,
  TextField,
} from '@mui/material';
import { Star, CheckCircle, Schedule, Cancel } from '@mui/icons-material';
import { premiumPartnerService, Agreement } from '@/services/premiumPartnerService';
import toast from 'react-hot-toast';

interface AgreementDetailDialogProps {
  open: boolean;
  agreement: Agreement;
  onClose: () => void;
  onUpdate: () => void;
}

export const AgreementDetailDialog: React.FC<AgreementDetailDialogProps> = ({
  open,
  agreement,
  onClose,
  onUpdate
}) => {
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [terminationReason, setTerminationReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleApprove = async () => {
    const adminUserId = localStorage.getItem('userId');
    if (!adminUserId) {
      toast.error('Admin user ID not found');
      return;
    }

    if (!confirm('Approve this agreement? This will upgrade the host to Premium tier and activate custom rates.')) {
      return;
    }

    setProcessing(true);
    try {
      const response = await premiumPartnerService.approveAgreement(
        agreement.id,
        adminUserId,
        undefined,
        'Approved via admin dashboard'
      );

      if (response.success) {
        toast.success('Agreement approved successfully!');
        onUpdate();
        onClose();
      } else {
        toast.error(response.error || 'Failed to approve agreement');
      }
    } catch (error) {
      console.error('Error approving agreement:', error);
      toast.error('An error occurred while approving the agreement');
    } finally {
      setProcessing(false);
    }
  };

  const handleTerminate = async () => {
    if (!terminationReason.trim()) {
      toast.error('Please provide a termination reason');
      return;
    }

    const adminUserId = localStorage.getItem('userId');
    if (!adminUserId) {
      toast.error('Admin user ID not found');
      return;
    }

    setProcessing(true);
    try {
      const response = await premiumPartnerService.terminateAgreement(
        agreement.id,
        adminUserId,
        terminationReason,
        false
      );

      if (response.success) {
        toast.success('Agreement terminated successfully');
        onUpdate();
        onClose();
      } else {
        toast.error(response.error || 'Failed to terminate agreement');
      }
    } catch (error) {
      console.error('Error terminating agreement:', error);
      toast.error('An error occurred while terminating the agreement');
    } finally {
      setProcessing(false);
      setTerminateDialogOpen(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'pending': return <Schedule sx={{ color: 'warning.main' }} />;
      default: return <Cancel sx={{ color: 'error.main' }} />;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Premium Partner Agreement</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {getStatusIcon(agreement.status)}
              <Chip
                label={agreement.status}
                color={agreement.status === 'active' ? 'success' : agreement.status === 'pending' ? 'warning' : 'error'}
                size="small"
                variant="filled"
                sx={{ textTransform: 'capitalize', fontWeight: 600 }}
              />
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Partner Information */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Partner Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Partner Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{agreement.host.fullName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body1">{agreement.host.email}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Partner Tier</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {agreement.host.partnerTier === 'premium' && <Star sx={{ fontSize: 16, color: 'gold' }} />}
                  <Chip
                    label={agreement.host.partnerTier}
                    size="small"
                    color={agreement.host.partnerTier === 'premium' ? 'secondary' : 'default'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Vehicle Count</Typography>
                <Typography variant="body1">{agreement.host.vehicleCount} vehicles</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Commission Structure */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Commission Structure
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Platform Fee</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {agreement.commission.platformFeePercentage}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Host keeps {(100 - agreement.commission.platformFeePercentage).toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Booking Fee</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {agreement.commission.bookingFeePercentage}%
                </Typography>
                <Typography variant="caption" color="text.secondary">Added to renter</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Contract Period */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Contract Period
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Start Date</Typography>
                <Typography variant="body1">{new Date(agreement.contract.startDate).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">End Date</Typography>
                <Typography variant="body1">
                  {agreement.contract.endDate ? new Date(agreement.contract.endDate).toLocaleDateString() : 'Ongoing'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Auto-Renewal</Typography>
                <Typography variant="body1">{agreement.contract.autoRenewal ? 'Yes' : 'No'}</Typography>
              </Grid>
            </Grid>

            {/* Account Manager */}
            {agreement.accountManager && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Account Manager
                </Typography>
                <Typography variant="body1">
                  {agreement.accountManager.firstName} {agreement.accountManager.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">{agreement.accountManager.email}</Typography>
              </>
            )}

            {/* Internal Notes */}
            {agreement.internalNotes && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Internal Notes
                </Typography>
                <Alert severity="info">
                  <Typography variant="body2">{agreement.internalNotes}</Typography>
                </Alert>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {agreement.status === 'active' && (
            <Button
              onClick={() => setTerminateDialogOpen(true)}
              color="error"
              disabled={processing}
            >
              Terminate
            </Button>
          )}
          {agreement.status === 'pending' && (
            <Button
              onClick={handleApprove}
              variant="contained"
              color="success"
              disabled={processing}
            >
              {processing ? 'Approving...' : 'Approve Agreement'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Terminate Confirmation Dialog */}
      <Dialog open={terminateDialogOpen} onClose={() => setTerminateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Terminate Agreement</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This will terminate the agreement and downgrade the partner tier. This action cannot be undone.
          </Alert>
          <TextField
            fullWidth
            label="Termination Reason"
            required
            multiline
            rows={4}
            value={terminationReason}
            onChange={(e) => setTerminationReason(e.target.value)}
            placeholder="Provide a detailed reason for termination..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleTerminate}
            color="error"
            variant="contained"
            disabled={processing || !terminationReason.trim()}
          >
            {processing ? 'Terminating...' : 'Terminate Agreement'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
